import React from 'react';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';

import {
  capitalizeFirst,
  formatCurrency,
  formatDate,
  formatSignedCurrency,
  formatSignedPercent,
  getChangeColor,
  getPriceDirection,
  getPriceDirectionLabel,
} from './formatters/toursPricingUtils';
import {
  APPLIED_PERCENT_MAX,
  APPLIED_PERCENT_MIN,
  buildAppliedPreview,
  getSignedValueColor,
  validateAppliedPercentInput,
} from './toursPricingApply';
import { LereToursPricingSuggestion } from './types';

export default function ToursPricingApplyDialog({
  applyDialogRow,
  closeApplyDialog,
  appliedPercentInput,
  setAppliedPercentInput,
  isApplying,
  handleApplyDialogConfirm,
  note,
  setNote,
}: {
  applyDialogRow: LereToursPricingSuggestion | null;
  closeApplyDialog: () => void;
  appliedPercentInput: string;
  setAppliedPercentInput: (value: string) => void;
  isApplying: boolean;
  handleApplyDialogConfirm: () => void;
  note?: string;
  setNote?: (value: string) => void;
}) {
  const appliedPercentValidation = validateAppliedPercentInput(appliedPercentInput);
  const applyDialogPreview =
    applyDialogRow && appliedPercentValidation.value != null
      ? buildAppliedPreview(applyDialogRow, appliedPercentValidation.value)
      : null;
  return (
    <Dialog
      open={Boolean(applyDialogRow)}
      onClose={closeApplyDialog}
      fullWidth
      maxWidth="sm"
      aria-labelledby="tours-pricing-apply-dialog-title"
    >
      <DialogTitle id="tours-pricing-apply-dialog-title">Apply pricing change</DialogTitle>
      {applyDialogRow && (
        <>
          <DialogContent>
            <Stack spacing={1.5}>
              <Typography variant="body2">
                Apply a pricing change for "{applyDialogRow.offer_name}" (departure{' '}
                {formatDate(applyDialogRow.departure_date)}).
              </Typography>
              <Typography variant="body2">
                Suggested change: {formatSignedPercent(applyDialogRow.suggested_price_change_percent)} (
                <Typography
                  component="span"
                  variant="body2"
                  color={getChangeColor(getPriceDirection(applyDialogRow))}
                  fontWeight="medium"
                >
                  {getPriceDirectionLabel(getPriceDirection(applyDialogRow))}
                </Typography>
                ) from base price {formatCurrency(applyDialogRow.original_tour_price)} to proposed live price{' '}
                {formatCurrency(applyDialogRow.suggested_after_price)}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Applying this will overwrite any existing surcharge or discount. Final live price is recalculated from
                base price + applied change.
              </Typography>
              <TextField
                type="number"
                label="Price change percent (%)"
                value={appliedPercentInput}
                onChange={(event) => setAppliedPercentInput(event.target.value)}
                error={Boolean(appliedPercentValidation.error)}
                helperText={
                  appliedPercentValidation.error ??
                  `Allowed range: ${APPLIED_PERCENT_MIN}% to ${APPLIED_PERCENT_MAX}%. Example: 3 means +3%.`
                }
                slotProps={{
                  htmlInput: { min: APPLIED_PERCENT_MIN, max: APPLIED_PERCENT_MAX, step: 0.1 },
                }}
                fullWidth
              />
              {setNote && (
                <TextField
                  label="Note"
                  value={note ?? ''}
                  onChange={(event) => setNote(event.target.value)}
                  multiline
                  fullWidth
                />
              )}
              {applyDialogPreview && (
                <Stack spacing={0.5} sx={{ pt: 0.5 }}>
                  <Typography variant="body2">
                    Applied action type:{' '}
                    <Typography
                      component="span"
                      variant="body2"
                      color={getChangeColor(applyDialogPreview.applied_action_type)}
                      fontWeight="medium"
                    >
                      {capitalizeFirst(applyDialogPreview.applied_action_type)}
                    </Typography>
                  </Typography>
                  <Typography
                    variant="body2"
                    color={getSignedValueColor(applyDialogPreview.applied_price_change_percent)}
                    fontWeight="medium"
                  >
                    Applied change: {formatSignedPercent(applyDialogPreview.applied_price_change_percent)}
                  </Typography>
                  <Typography variant="body2">
                    Applied live price: {formatCurrency(applyDialogPreview.applied_after_price)}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={getSignedValueColor(applyDialogPreview.netVsCurrentAmount)}
                    fontWeight="medium"
                  >
                    Direction from current price: {formatSignedCurrency(applyDialogPreview.netVsCurrentAmount)}
                    {applyDialogPreview.netVsCurrentPercent != null && (
                      <> ({formatSignedPercent(applyDialogPreview.netVsCurrentPercent)})</>
                    )}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeApplyDialog} disabled={isApplying}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyDialogConfirm}
              disabled={isApplying || Boolean(appliedPercentValidation.error)}
            >
              Confirm
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
